import type {ReactElement} from 'react';
import {useRef} from 'react';
import React from 'react';
import {ControlledSlider} from '@Components/controlled-slider';
import {Text, TextSize} from '@Components/text';
import {InputField} from '@Components/input-field-v2';
import {InputFieldSize} from '@Components/input-field-v2/input-field.types';
import styles from './download-bleed-option.module.scss';
import {useDownloadPanelData} from '@Components/download-panel/download-panel.context';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION, DROPDOWN_SIZE} from '@Components/dropdown-v2/dropdown.types';
import {DropdownDefaultSelectBox} from '@Components/dropdown-v2/components/dropdown-default-select-box';
import {DownloadBleedOptionTypes} from '@Components/download-panel/components/download-bleed-option/download-bleed-option.types';
import {ControlledList} from '@Components/controlled-list';
import type {ControlledListItemType} from '@Components/controlled-list/controlled-list.types';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import {isEditorMobileVariant} from '@Components/poster-editor/library/poster-editor-library';
import {closeModal} from '@Components/modal-container/modal-container-reducer';
import {MODALS_IDS} from '@Components/modal-container';
import {useAppDispatch} from '@/hooks';
import {noop} from '@Utils/general.util';

interface DownloadBleedOptionProps {
  onSlideCallback?(): void;
  onSlideEndedCallback?(): void;
}

export function DownloadBleedOption({onSlideCallback = noop, onSlideEndedCallback = noop}: DownloadBleedOptionProps): ReactElement {
  const {bleed, setBleed, bleedUnit, setBleedUnit} = useDownloadPanelData();
  const timer = useRef<NodeJS.Timeout>(undefined);
  const dispatch = useAppDispatch();
  const onBleedChange = (value: number): void => {
    onSlideCallback();
    setBleed(value);

    if (value > 10 || value < 0) {
      if (timer.current) {
        clearTimeout(timer.current);
      }
      const valueWithConstraints = Math.max(0, Math.min(10, value));
      timer.current = setTimeout((): void => {
        setBleed(valueWithConstraints);
      }, 300);
    }
  };

  const getBleedUnitSelector = (): ReactElement => {
    return <DropdownDefaultSelectBox className={styles.selector} size={DROPDOWN_SIZE.XSMALL} text={getValueToShow()} />;
  };

  const getValueToShow = (): string => {
    switch (bleedUnit) {
      case DownloadBleedOptionTypes.CM:
        return 'cm';
      case DownloadBleedOptionTypes.INCHES:
        return 'inch';
      case DownloadBleedOptionTypes.MM:
        return 'mm';
      default:
        return '';
    }
  };

  const getItems = (): ControlledListItemType[] => {
    return [
      {
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_3,
        text: 'inch',
        id: DownloadBleedOptionTypes.INCHES,
        isSelected: bleedUnit === DownloadBleedOptionTypes.INCHES,
      },
      {
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_3,
        text: 'cm',
        id: DownloadBleedOptionTypes.CM,
        isSelected: bleedUnit === DownloadBleedOptionTypes.CM,
      },
      {
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_3,
        text: 'mm',
        id: DownloadBleedOptionTypes.MM,
        isSelected: bleedUnit === DownloadBleedOptionTypes.MM,
      },
    ];
  };

  const getBleedUnitPopup = (): ReactElement => {
    return (
      <ControlledList
        onClick={(listItemId: DownloadBleedOptionTypes): void => {
          setBleedUnit(listItemId);
          if (isEditorMobileVariant()) {
            dispatch(closeModal(MODALS_IDS.MORE_OPTIONS_MODAL));
          }
        }}
        items={getItems()}
      />
    );
  };

  return (
    <div className="flex flex-v-row">
      <div className="flex-h-row flex-justify-between flex-items-center spacing-m-b-2">
        <Text val={window.i18next.t('pmwjs_bleed')} bold size={TextSize.XSMALL} />
        <div className="flex-h-row">
          <InputField step={0.1} selectOnFocus className={`${styles.bleedInput} spacing-m-r-2`} size={InputFieldSize.XSMALL} input={bleed} onInputChange={onBleedChange} />
          <Dropdown
            popupClassName={styles.popup}
            popUpHasCustomWidth
            position={DROPDOWN_POSITION.FIXED}
            alignment={ALIGNMENT_TYPE.BOTTOM_END}
            selector={getBleedUnitSelector()}
            popup={getBleedUnitPopup()}
            popupSpacingClass="spacing-p-t-2 spacing-p-r-2 spacing-p-l-2"
            showModalForSmallScreen
            modalTitle={window.i18next.t('pmwjs_units')}
          />
        </div>
      </div>
      <ControlledSlider onSlideEnded={onSlideEndedCallback} step={0.1} min={0} max={10} value={bleed} onSlide={onBleedChange} />
    </div>
  );
}
